import {
  VALIDATION_MESSAGES,
  isValidSsn,
  parsePhoneNumber,
} from '@zorro/common-utils';
import * as yup from 'yup';

export const getPersonFormSchema = (
  isFinalizationMode: boolean,
  requireIdentityFields: boolean = false
) => {
  return yup.object({
    isDuplicateMode: yup.boolean().required(),
    ...(isFinalizationMode
      ? {
          firstName: yup.string().when('isDuplicateMode', {
            is: false,
            then: (schema) =>
              schema.required(VALIDATION_MESSAGES.firstNameRequired),
            otherwise: (schema) => schema.notRequired(),
          }),
          lastName: yup.string().when('isDuplicateMode', {
            is: false,
            then: (schema) =>
              schema.required(VALIDATION_MESSAGES.lastNameRequired),
            otherwise: (schema) => schema.notRequired(),
          }),
          phone: yup.string().when('isDuplicateMode', {
            is: false,
            then: (schema) =>
              schema
                .required(VALIDATION_MESSAGES.phoneNumberRequired)
                /**
                 * We expect a valid US phone number entered by the user
                 * and react-phone-number-input automatically adds +1 prefix to it
                 */
                .test(
                  'valid-phone',
                  VALIDATION_MESSAGES.phoneNumberValid,
                  (value) => parsePhoneNumber(value) !== undefined
                ),

            otherwise: (schema) => schema.notRequired(),
          }),
          email: yup.string().when('isDuplicateMode', {
            is: false,
            then: (schema) =>
              schema
                .required(VALIDATION_MESSAGES.emailRequired)
                .email(VALIDATION_MESSAGES.emailValid),
            otherwise: (schema) => schema.notRequired(),
          }),
          address: requireIdentityFields
            ? yup.string().required(VALIDATION_MESSAGES.addressRequired)
            : yup.string().optional(),
          dateOfBirth: requireIdentityFields
            ? yup.date().required(VALIDATION_MESSAGES.dateOfBirthRequired)
            : yup.date().optional(),
          ssn: requireIdentityFields
            ? yup
                .string()
                .test(
                  'valid-ssn',
                  VALIDATION_MESSAGES.ssnNumberValid,
                  (value) => (value ? isValidSsn(value) : true)
                )
                .required(VALIDATION_MESSAGES.ssnRequired)
            : yup.string().optional(),
          isAdmin: yup.boolean().when('isDuplicateMode', {
            is: false,
            then: (schema) =>
              schema.required(VALIDATION_MESSAGES.isAdminRequired),
            otherwise: (schema) => schema.notRequired(),
          }),
        }
      : {
          firstName: yup.string(),
          lastName: yup.string(),
          phone: yup
            .string()
            .nullable()
            /**
             * We expect a valid US phone number entered by the user
             * and react-phone-number-input automatically adds +1 prefix to it
             */
            .test(
              'valid-phone',
              VALIDATION_MESSAGES.phoneNumberValid,
              (value) => {
                if (!value) return true;
                return parsePhoneNumber(value) !== undefined;
              }
            ),
          email: yup.string().email(VALIDATION_MESSAGES.emailValid),
          address: yup.string().optional(),
          dateOfBirth: yup.date().optional(),
          ssn: yup
            .string()
            .test('valid-ssn', VALIDATION_MESSAGES.ssnNumberValid, (value) =>
              value ? isValidSsn(value) : true
            ),
          isAdmin: yup.boolean(),
        }),
  });
};

export type PersonFormItem = yup.InferType<
  ReturnType<typeof getPersonFormSchema>
>;

export const getDefaultPersonFormItem = (
  isAdmin = false,
  shouldShowIdentityFields = false
): PersonFormItem => {
  return {
    firstName: '',
    lastName: '',
    phone: '',
    email: '',
    isAdmin,
    isDuplicateMode: false,
    ...(shouldShowIdentityFields
      ? { address: '', dateOfBirth: undefined, ssn: '' }
      : {}),
  };
};
